import { useMutation } from '@apollo/client';
import { CREATE_ANALYTICS_EVENT } from '@base/queries/LearnerQueries';
import {
  CompleteModal,
  PublicSimPlayer,
} from '@core/components/Unity/components/SimulationPublicPlayer/SimulationPublicPlayer.styled';
import tracker from '@core/helpers/tracker';
import { Button } from '@virtidev/toolbox';
import { useCallback, useContext, useEffect, useState } from 'react';
import { SimulationPlayerManager } from '@core/components/Unity/Unity.types';
import { IsMobile } from '@base/utility/BrowserCheck';
import { StyledMessage } from '@core/components/Unity/Unity.styled';
import { useLocation } from 'react-router-dom';
import { deepLinkValueContext } from '@core/components/DeepLink/DeepLinkProvider';
import { generateDeepLink } from '@core/components/DeepLink/helpers/generateDeepLink';
import mixpanel from 'mixpanel-browser';
import { sendSimSCORMComplete } from '@core/components/Unity/helpers/SimulationSCORMEventEmitters';
import { useIsIframe } from '@core/helpers/useIsIframe';

export const SimulationPublicPlayer = ({ simulation, sharetoken }) => {
  const [manager, setManager] = useState(
    /** @type {SimulationPlayerManager | null} */ (null)
  );
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [show, setShow] = useState(false);
  const [visible, setVisible] = useState(true);
  const embedded = useIsIframe();

  const [sendAnalytics] = useMutation(CREATE_ANALYTICS_EVENT);

  const closeModal = useCallback(() => setShow(false), []);

  const { pathname } = useLocation();
  const deepLink = useContext(deepLinkValueContext);

  const goToLink = useCallback(() => {
    window.onbeforeunload = null;
    if (deepLink) {
      window.location.href = deepLink;
    } else {
      window.location.href = generateDeepLink(pathname);
    }
  }, [deepLink, pathname]);

  const handleEnd = useCallback(() => {
    document.exitFullscreen().catch(() => null);

    setAnalyticsData(null);
  }, []);

  const handleComplete = useCallback(() => {
    setCompleted(true);
    setShow(true);

    const org = simulation.Organisation || {};

    tracker.track('share_content_completed', {
      type: 'simulation',
      simulation_id: `${simulation.ID}`,
      token: sharetoken,
      org_id: org.ID,
      org_name: org.Name,
      customer_stage: org.CustomerStage,
    });
  }, [simulation?.ID, simulation?.Organisation, sharetoken]);

  const handleExitSimData = useCallback((data) => {
    setAnalyticsData(data);
  }, []);

  const handlePlayerLoaded = useCallback(() => {
    const org = simulation.Organisation || {};

    tracker.track('share_content_open', {
      type: 'simulation',
      simulation_id: `${simulation.ID}`,
      token: sharetoken,
      org_id: org.ID,
      org_name: org.Name,
      customer_stage: org.CustomerStage,
    });

    const id = mixpanel.get_distinct_id();
    manager && manager.setMixpanelId(id);

    setLoaded(true);
  }, [manager, simulation?.ID, simulation?.Organisation, sharetoken]);

  const handleScoreAvailable = useCallback((score) => {
    sendSimSCORMComplete(score);
  }, []);

  useEffect(() => {
    if (!loaded || completed || !visible) {
      return;
    }

    window.onbeforeunload = () => {
      if (analyticsData) {
        sendAnalytics({ variables: { Input: JSON.parse(analyticsData) } });
      }
      return embedded
        ? 'Interactive video in progress, are you sure about leaving?'
        : null;
    };

    return () => {
      window.onbeforeunload = null;
    };
  }, [analyticsData, sendAnalytics, loaded, completed, embedded, visible]);

  if (IsMobile()) {
    return (
      <StyledMessage>
        Unfortunately, web preview is currently only available in latest version
        of Google Chrome, Mozilla Firefox, and Microsoft Edge on a computer.
        {!embedded && (
          <Button color="primary" onClick={goToLink}>
            Use the Virti App
          </Button>
        )}
      </StyledMessage>
    );
  }

  return (
    <>
      <CompleteModal
        show={show && !embedded}
        onHide={closeModal}
        title="Create your own Interactive video"
        footer={
          <>
            <Button color="turquoise" design="outline" onClick={closeModal}>
              Close
            </Button>
            <Button
              as="a"
              color="turquoise"
              type="link"
              title="Sign up to a free trial of Virti."
              href="/register"
            >
              Create account
            </Button>
          </>
        }
      >
        Create a Virti account and start creating your own interactive videos.
      </CompleteModal>
      <PublicSimPlayer
        simulationData={simulation}
        show
        onEnd={handleEnd}
        onComplete={handleComplete}
        onExitSimData={handleExitSimData}
        onPlayerLoaded={handlePlayerLoaded}
        onScoreAvailable={handleScoreAvailable}
        onPlayerVisible={setVisible}
        setManager={setManager}
        sharetoken={sharetoken}
        publicPlayer
      />
    </>
  );
};

export default SimulationPublicPlayer;
